<template lang="pug">
  section
    h2.heading Profile
    .profile-data
      dl.half
        dt Member Info
        dd
          table
            tr
              th.small DOB/Age
              td {{ dateOfBirth(patient) }} {{ adolescent  }}
            tr
              th.small Gender
              td {{ gender }}
            tr
              th.small Sexual Orientation
              td
                div(v-for="sexualOrientation in sexualOrientations") {{ sexualOrientation }}
            tr
              th.small Pronouns
              td
                div(v-for="pronoun in pronouns") {{ pronoun }}
            tr
              th.small Language
              td {{ userLanguage }}
            tr
              th.small Registration
              td {{ patient.createdOn | shortDate }}
                span.error(v-if="!patient.profileComplete") *Incomplete
            tr(v-if="patient.isLivongoMember != null")
              th.small Livongo Member
              td(v-if="patient.isLivongoMember")
                | Yes
              td(v-else)
                | No
            tr(v-if="patient.customPartnerFields && patient.customPartnerFields.length > 0")
              th.small Partner Fields
              td
                ul.default
                  li(v-for="field in patient.customPartnerFields")
                    | {{ field.signUpFieldName}}: {{ field.response }}
      dl.half
        dt Customization
        dd
          table
            tr
              th Current Focus
              td {{ patient.currentFocus }}
            tr
              th Other Programs
              td
                ul.default(v-if="wellnessPrograms")
                  li(v-for="tag in wellnessPrograms") {{ tag.label }}
            tr
              th Reason for joining
              td {{ patient.whatBringsYou }}
            tr
              th Teletherapy eligible
              td
                div {{ patient.teletherapyEligible ? 'Yes' : 'No' }}
      dl.full
        dt Personalization Preferences
        dd
          ul.inline(v-if="interests")
            li(v-for="tag in interests") {{ tag.label }}

      dl.full
        dt Goals
        dd(v-for="goal in patient.goals")
          blockquote
            p &ldquo;{{ goal }}&rdquo;
        dd(v-if="!patient.goals || patient.goals.length === 0")
          | No goals entered
      interaction-history(:loginHistory="patient.loginHistory" :patientAge="patient.age" :patientGender="patient.gender")
      dl.review-history
        dt Review History
        dd(v-if="patient.reviewHistory && patient.reviewHistory.length > 0")
          table
            tr(v-for="review in patient.reviewHistory")
              th.small {{ review.modifiedOn | shortDate }}
              td {{ review.partnerName }}
              td {{ review.partnerEmail }}
        dd(v-else)
          p.no-results No Review History
</template>

<script>
import InteractionHistory from '@/components/patient/common/InteractionHistory';
import profileMixin from '@/mixins/profileInfo.js';
import _ from 'lodash';

const ADDITIONAL_GENDER = 'Additional gender category, please specify';

export default {
  name: 'PatientProfile',
  props: [ 'patient' ],
  components: {
    InteractionHistory
  },
  computed: {
    userLanguage() {
      if (_.startsWith(this.patient.language, 'es-')) {
        return 'Spanish';
      }
      return 'English';
    },
    wellnessPrograms() {
      const currentFocus = _.get(this.patient, 'currentFocus');
      if (this.patient.interests) {
        return this.patient.interests.filter(tag => tag.program && tag.label !== currentFocus);
      }
      return null;
    },
    interests() {
      if (this.patient.interests) {
        return this.patient.interests.filter(tag => !tag.program);
      }
      return null;
    },
    adolescent() {
      return this.patient.isAdolescent ? '(adolescent)' : '';
    },
    gender() {
      return this.patient.gender === ADDITIONAL_GENDER
        ? this.patient.otherGender : this.patient.gender;
    },
    sexualOrientations() {
      const { sexualOrientation, otherSexualOrientation } = this.patient;
      // Splits string by commas except if comma is followed by a space
      // (this means comma is part of the value, not being used to delimit between values)
      const displayArr = (sexualOrientation && sexualOrientation.split(/,(?! )/gm)) || [];

      return otherSexualOrientation ? displayArr.concat([otherSexualOrientation]) : displayArr;
    },
    pronouns() {
      const { pronouns, otherPronouns } = this.patient;
      const displayArr = (pronouns && pronouns.split(/,(?! )/gm)) || [];

      return otherPronouns ? displayArr.concat([otherPronouns]) : displayArr;
    }
  },
  mixins: [ profileMixin ]
};
</script>

<style lang="scss" scoped>
  .profile-data {
    margin: 0 auto $base-spacing;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }
  .half {
    width: 100%;
    @include media-query-bp($bp4) {
      width: 48%;
    }
  }
  dl {
    @include zero;
    margin-bottom: $base-spacing;
    ul {
      margin-bottom: 0;
    }
  }
  dd, dt {
    @include zero;
  }
  dt {
    text-transform: uppercase;
  }
  .error {
    color: $error-color;
  }
  dl.full {
    width: 100%;
    dt {
      margin-bottom: $xs-spacing;
    }
  }
  table {
    @include zero;
    th, td {
      border: none;
      padding: $small-spacing;
      white-space: pre-wrap;
      vertical-align: top;
      border-top: 1px solid $base-border-color;
    }
    .small {
      width: 8em;
    }
  }

  .inline {
    display: block;
    width: 100%;
    margin: 0 auto $small-spacing;
    li {
      display: inline-block;
      margin-bottom: 0.25em;
      &:after {
        content: ",";
        margin-right: 0.25em;
      }
      &:last-of-type {
        &:after {
          content: "";
          margin-right: 0;
        }
      }
    }
  }

  .review-history {
    margin: 0 auto;
    background: $md-grey-50;
    width: 100%;
    display: block;
    dt {
      padding: $xs-spacing;
      border-top: 2px solid $action-color;
      color: $action-color;
    }
    p.no-results {
      padding: $xs-spacing;
      padding-top: 0;
    }
    td {
      @include contain-text;
    }
  }
</style>
